/* eslint-disable no-console */
import { cn } from '@/utils/cn';
import { XIcon } from 'lucide-react';
import React, { memo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { handleDomBubbleEvent } from '@/utils/dom';

interface BaseModalProps {
  isOpen?: boolean;
  title?: string;
  description?: string;
  descriptionHtml?: React.ReactNode;
  children: React.ReactNode;
  onClose?: () => void;
  isCloseOutside?: boolean;
  onClickOutside?: () => void;
  isButtonClose?: boolean;
  className?: string;
  closeButtonClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

function BaseModal({
  isOpen,
  title,
  description,
  descriptionHtml,
  children,
  onClose,
  isCloseOutside = true,
  onClickOutside,
  isButtonClose = true,
  className,
  closeButtonClassName,
  titleClassName,
  descriptionClassName,
}: BaseModalProps) {
  // HOOKS
  const modalRootRef = useRef<HTMLElement | null>(null);

  // Get portal root element with error handling
  if (!modalRootRef.current) {
    modalRootRef.current = document.getElementById('modal-root');
  }

  // Early return if portal root doesn't exist
  if (!modalRootRef.current) {
    console.warn('Modal root element not found');
    return null;
  }

  // RENDER
  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        zIndex: 999,
      }}
      className="fixed inset-0 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        e.stopPropagation();
        onClickOutside?.();
        if (isCloseOutside) {
          onClose?.();
        }
      }}
    >
      <div
        className={cn(
          'p-[48px] rounded-[16px] relative border border-[#D9D9D9] bg-[#FFF] w-[500px] max-h-[90vh] flex flex-col',
          className
        )}
        style={{
          boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isButtonClose ? (
          <button
            type="button"
            onClick={(e) => {
              handleDomBubbleEvent(e);
              onClose?.();
            }}
            className={cn(
              'absolute top-[24px] right-[24px] bg-[#F5F5F5] rounded-full w-[24px] h-[24px] flex items-center justify-center z-10 cursor-pointer',
              closeButtonClassName
            )}
          >
            <XIcon className="text-[#0C8DBF] w-[16px] h-[16px]" />
          </button>
        ) : null}
        {(title || description || descriptionHtml) && (
          <div className="flex-shrink-0">
            {title ? (
              <h2 className={cn('text-[16px] font-bold leading-[21px] text-[#0C8DBF]', titleClassName)}>{title}</h2>
            ) : null}
            {description && !descriptionHtml ? (
              <p className={cn('text-[14px] font-normal leading-[20px] text-[#9D9D9C] mt-[8px]', descriptionClassName)}>
                {description}
              </p>
            ) : null}
            {!description && descriptionHtml ? descriptionHtml : null}
          </div>
        )}
        <div className="overflow-y-auto flex-1 min-h-0">{children}</div>
      </div>
    </div>,
    modalRootRef.current
  );
}

export default memo(BaseModal);
